<h2 mat-dialog-title>Developer Tools</h2>

<mat-dialog-content class="csv-upload-content">
  <h3>Upload CSV File</h3>
  <div class="upload-section">
    <p>Select a CSV file to upload and migrate events to the database.</p>
    
    <div class="file-input-container">
      <input 
        type="file" 
        #fileInput 
        accept=".csv" 
        (change)="onFileSelected($event)"
        class="file-input"
        id="csv-file-input">
      <label for="csv-file-input" class="file-input-label">
        <mat-icon>cloud_upload</mat-icon>
        Choose CSV File
      </label>
    </div>
    
    <div class="selected-file" *ngIf="selectedFile">
      <mat-icon>insert_drive_file</mat-icon>
      <span>{{ selectedFile.name }}</span>
      <span class="file-size">({{ getFileSize() }})</span>
    </div>
    
    <div class="processing-status" *ngIf="processing">
      <mat-spinner diameter="30"></mat-spinner>
      <div class="processing-details">
        <div class="current-step">{{ currentStep }}</div>
        <div class="progress-info" *ngIf="totalRows > 0">
          <span class="row-counter">Row {{ currentRowNumber }} of {{ totalRows }}</span>
          <span class="person-name" *ngIf="currentPersonName">({{ currentPersonName }})</span>
        </div>
        <mat-progress-bar 
          mode="determinate" 
          [value]="progressPercentage"
          *ngIf="totalRows > 0">
        </mat-progress-bar>
      </div>
    </div>
    
    <div class="results" *ngIf="processResults">
      <div class="success-message" *ngIf="processResults.success">
        <mat-icon color="primary">check_circle</mat-icon>
        <span>Successfully uploaded {{ processResults.processed }} events to database</span>
        <div *ngIf="processResults.duplicates && processResults.duplicates > 0" class="duplicates-info">
          ({{ processResults.duplicates }} duplicates filtered out)
        </div>
      </div>
      
      <div class="error-message" *ngIf="!processResults.success">
        <mat-icon color="warn">error</mat-icon>
        <span>Error: {{ processResults.error }}</span>
      </div>
    </div>
  </div>

  <div class="migration-section">
    <h3>One-time Requestor/Event Leader Migration</h3>
    <p>
      Updates existing events that only have requestor/event leader email to the new JSON format
      (gmid, email, name) using Graph lookups.
    </p>

    <div class="processing-status" *ngIf="migrationProcessing">
      <mat-spinner diameter="30"></mat-spinner>
      <div class="processing-details">
        <div class="current-step">{{ migrationStep }}</div>
        <div class="progress-info" *ngIf="migrationTotal > 0">
          <span class="row-counter">Event {{ migrationCurrent }} of {{ migrationTotal }}</span>
        </div>
        <mat-progress-bar
          mode="determinate"
          [value]="migrationProgress"
          *ngIf="migrationTotal > 0">
        </mat-progress-bar>
      </div>
    </div>

    <div class="results" *ngIf="migrationResults">
      <div class="success-message" *ngIf="migrationResults.success">
        <mat-icon color="primary">check_circle</mat-icon>
        <span>
          Migration completed. Updated {{ migrationResults.updated }}, skipped {{ migrationResults.skipped }}.
        </span>
        <div *ngIf="migrationResults.errors > 0" class="duplicates-info">
          ({{ migrationResults.errors }} errors)
        </div>
      </div>

      <div class="error-message" *ngIf="!migrationResults.success">
        <mat-icon color="warn">error</mat-icon>
        <span>Error: {{ migrationResults.error || 'Migration failed' }}</span>
      </div>
    </div>
  </div>

  <div class="diagnostics-section">
    <div class="diagnostics-header">
      <div>
        <h3>Migration Diagnostics</h3>
        <p>Overview of records that still need migration and those already in the new format.</p>
      </div>
      <div class="diagnostics-actions">
        <mat-form-field appearance="outline" class="reason-filter">
          <mat-label>Reason</mat-label>
          <mat-select [(ngModel)]="selectedReason">
            <mat-option *ngFor="let reason of reasonOptions" [value]="reason">
              {{ reason }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="person-filter">
          <mat-label>Person</mat-label>
          <mat-select [(ngModel)]="selectedPerson">
            <mat-option *ngFor="let person of personOptions" [value]="person">
              {{ person }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="person-filter">
          <mat-label>Person data</mat-label>
          <mat-select [(ngModel)]="selectedPersonData">
            <mat-option *ngFor="let option of personDataOptions" [value]="option">
              {{ option }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <button
          mat-stroked-button
          color="primary"
          (click)="refreshMigrationDiagnostics()"
          [disabled]="migrationDiagnosticsLoading">
          <mat-icon *ngIf="!migrationDiagnosticsLoading">refresh</mat-icon>
          <mat-spinner *ngIf="migrationDiagnosticsLoading" diameter="18"></mat-spinner>
          {{ migrationDiagnosticsLoading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
    </div>

    <div class="diagnostics-summary">
      <div class="summary-card">
        <div class="summary-label">Total events</div>
        <div class="summary-value">{{ migrationDiagnostics.total }}</div>
      </div>
      <div class="summary-card pending">
        <div class="summary-label">Needs migration</div>
        <div class="summary-value">{{ migrationDiagnostics.pendingCount }}</div>
      </div>
      <div class="summary-card not-relevant">
        <div class="summary-label">Not relevant/testing</div>
        <div class="summary-value">{{ migrationDiagnostics.notRelevantCount }}</div>
      </div>
      <div class="summary-card migrated">
        <div class="summary-label">Migrated</div>
        <div class="summary-value">{{ migrationDiagnostics.migratedCount }}</div>
      </div>
      <div class="summary-card timestamp" *ngIf="migrationDiagnosticsLastUpdated">
        <div class="summary-label">Last updated</div>
        <div class="summary-value">
          {{ migrationDiagnosticsLastUpdated | date:'short' }}
        </div>
      </div>
    </div>

    <div class="diagnostics-chart">
      <div class="chart-title">Migration status</div>
      <div class="chart-body" *ngIf="migrationDiagnostics.total > 0">
        <div class="chart-bars">
          <div class="chart-bar pending" [style.width.%]="migrationDiagnostics.pendingPercent">
            <span>{{ migrationDiagnostics.pendingPercent }}%</span>
          </div>
          <div class="chart-bar not-relevant" [style.width.%]="migrationDiagnostics.notRelevantPercent">
            <span>{{ migrationDiagnostics.notRelevantPercent }}%</span>
          </div>
          <div class="chart-bar migrated" [style.width.%]="migrationDiagnostics.migratedPercent">
            <span>{{ migrationDiagnostics.migratedPercent }}%</span>
          </div>
        </div>
        <div class="chart-legend">
          <div class="legend-item">
            <span class="legend-swatch pending"></span>
            Needs migration ({{ migrationDiagnostics.pendingCount }})
          </div>
          <div class="legend-item">
            <span class="legend-swatch not-relevant"></span>
            Not relevant/testing ({{ migrationDiagnostics.notRelevantCount }})
          </div>
          <div class="legend-item">
            <span class="legend-swatch migrated"></span>
            Migrated ({{ migrationDiagnostics.migratedCount }})
          </div>
        </div>
      </div>
      <div class="chart-empty" *ngIf="migrationDiagnostics.total === 0">
        No events loaded for diagnostics.
      </div>
    </div>

    <div class="diagnostics-chart">
      <div class="chart-title">Not migrated by reason</div>
      <div class="chart-subtitle">
        View: {{ selectedPerson }}{{ selectedReason !== 'All' ? ' | ' + selectedReason : '' }}
      </div>
      <div class="chart-body" *ngIf="getReasonChartData().length">
        <div class="reason-row" *ngFor="let reason of getReasonChartData()">
          <div class="reason-label">{{ reason.reason }}</div>
          <div class="reason-bar">
            <div class="reason-fill" [style.width.%]="reason.percent"></div>
          </div>
          <div class="reason-count">{{ reason.count }} ({{ reason.percent }}%)</div>
        </div>
      </div>
      <div class="chart-empty" *ngIf="!getReasonChartData().length">
        No pending reasons for the selected filters.
      </div>
    </div>

    <div class="diagnostics-lists">
      <details class="diagnostics-list">
        <summary>Records needing migration ({{ getFilteredPendingCount() }} of {{ migrationDiagnostics.pendingCount }})</summary>
        <div class="list-table" *ngIf="getFilteredPending().length">
          <div class="list-header">
            <span>Event</span>
            <span>Requestor</span>
            <span>Status</span>
            <span>Reason</span>
            <span>Event Leader</span>
            <span>Status</span>
            <span>Reason</span>
            <span>Actions</span>
          </div>
          <div class="list-row" *ngFor="let item of getFilteredPending()">
            <div class="event-cell">
              <span>#{{ item.eventId }} {{ item.requestTitle }}</span>
              <span class="test-chip" *ngIf="item.startDate">Start: {{ item.startDate | date:'shortDate' }}</span>
              <span class="test-chip" *ngIf="item.endDate">End: {{ item.endDate | date:'shortDate' }}</span>
              <span class="test-chip" *ngIf="item.vodExpirationDate">VOD: {{ item.vodExpirationDate | date:'shortDate' }}</span>
            </div>
            <span>{{ item.requestorDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.requestorStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.requestorStatus }}
            </span>
            <span class="reason-text">{{ item.requestorReason || '-' }}</span>
            <span>{{ item.eventLeaderDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.eventLeaderStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.eventLeaderStatus }}
            </span>
            <span class="reason-text">{{ item.eventLeaderReason || '-' }}</span>
            <button
              mat-stroked-button
              color="primary"
              type="button"
              (click)="migratePendingItem(item, $event)"
              [disabled]="migrationProcessing">
              Migrate
            </button>
          </div>
        </div>
        <div class="list-empty" *ngIf="!getFilteredPending().length">
          No pending records.
        </div>
      </details>

      <details class="diagnostics-list">
        <summary>Not relevant/testing ({{ migrationDiagnostics.notRelevantCount }})</summary>
        <div class="list-table" *ngIf="migrationDiagnostics.notRelevant.length">
          <div class="list-header">
            <span>Event</span>
            <span>Requestor</span>
            <span>Status</span>
            <span>Reason</span>
            <span>Event Leader</span>
            <span>Status</span>
            <span>Reason</span>
          </div>
          <div class="list-row" *ngFor="let item of migrationDiagnostics.notRelevant">
            <div class="event-cell">
              <span>#{{ item.eventId }} {{ item.requestTitle }}</span>
              <span class="test-chip" *ngIf="item.startDate">Start: {{ item.startDate | date:'shortDate' }}</span>
              <span class="test-chip" *ngIf="item.endDate">End: {{ item.endDate | date:'shortDate' }}</span>
              <span class="test-chip" *ngIf="item.vodExpirationDate">VOD: {{ item.vodExpirationDate | date:'shortDate' }}</span>
            </div>
            <span>{{ item.requestorDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.requestorStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.requestorStatus }}
            </span>
            <span class="reason-text">{{ item.requestorReason || '-' }}</span>
            <span>{{ item.eventLeaderDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.eventLeaderStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.eventLeaderStatus }}
            </span>
            <span class="reason-text">{{ item.eventLeaderReason || '-' }}</span>
          </div>
        </div>
        <div class="list-empty" *ngIf="!migrationDiagnostics.notRelevant.length">
          No not relevant/testing records.
        </div>
      </details>

      <details class="diagnostics-list">
        <summary>Records migrated ({{ migrationDiagnostics.migratedCount }})</summary>
        <div class="list-table" *ngIf="migrationDiagnostics.migrated.length">
          <div class="list-header">
            <span>Event</span>
            <span>Requestor</span>
            <span>Status</span>
            <span>Reason</span>
            <span>Event Leader</span>
            <span>Status</span>
            <span>Reason</span>
          </div>
          <div class="list-row" *ngFor="let item of migrationDiagnostics.migrated">
            <span>#{{ item.eventId }} {{ item.requestTitle }}</span>
            <span>{{ item.requestorDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.requestorStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.requestorStatus }}
            </span>
            <span class="reason-text">{{ item.requestorReason || '-' }}</span>
            <span>{{ item.eventLeaderDisplay || '-' }}</span>
            <span class="status-chip" [ngClass]="item.eventLeaderStatus === 'OK' ? 'ok' : 'warning'">
              {{ item.eventLeaderStatus }}
            </span>
            <span class="reason-text">{{ item.eventLeaderReason || '-' }}</span>
          </div>
        </div>
        <div class="list-empty" *ngIf="!migrationDiagnostics.migrated.length">
          No migrated records.
        </div>
      </details>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="onCancel()">Cancel</button>
  <button
    mat-stroked-button
    color="accent"
    (click)="runPeopleMigration()"
    [disabled]="processing || migrationProcessing">
    <mat-icon *ngIf="!migrationProcessing">sync</mat-icon>
    <mat-spinner *ngIf="migrationProcessing" diameter="20"></mat-spinner>
    {{ migrationProcessing ? 'Migrating...' : 'Run Migration' }}
  </button>
  <button 
    mat-raised-button 
    color="primary" 
    (click)="onProcess()" 
    [disabled]="!selectedFile || processing">
    <mat-icon *ngIf="!processing">upload</mat-icon>
    <mat-spinner *ngIf="processing" diameter="20"></mat-spinner>
    {{ processing ? 'Uploading...' : 'Upload to Database' }}
  </button>
</mat-dialog-actions>